import React, { FC, useCallback, useMemo } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { AviaTraveller, OrderStatus, ChangeTravellerDataTravellerAvailability } from '@websky/graphql';
import { useTheme } from '../../../theme';
import { getAdultAccompaniedMap, isTravellerOlderThanMinDate } from '../../utils';
import { ChangeLinkReturn } from '../../hooks';
import Passenger from '../Passenger/Passenger';
import Notification from '../../../BaseComponents/Notification/Notification';

interface Props extends ChangeLinkReturn {
	className?: string;
	orderStatus: OrderStatus;
	travellers: AviaTraveller[];
	changeTravellersAvailability?: ChangeTravellerDataTravellerAvailability[];
	goToChangePassenger?: (passengerId: string) => void;
}

const PassengersDetails: FC<Props> = ({
	className,
	orderStatus,
	travellers,
	changeTravellersAvailability,
	goToChangePassenger,
	onLinkTo,
	isLoading,
	onSetRelinkingMode,
	isRelinkingMode,
	relinkingToPassengerId,
	relinkingPassenger
}) => {
	const { PassengersDetails: css } = useTheme('PassengersDetails');
	const { t } = useTranslation('OrderCard');

	const isAvailableForRelinking = (traveller: AviaTraveller) => {
		const isAlreadyAccompanyInf = adultAccompaniedMap
			.get(traveller.id)
			?.some(accompanied => accompanied.type === 'INF');

		const isOlderThanMinDate = isTravellerOlderThanMinDate(traveller, relinkingPassenger);
		const isRelinkingCldOrTravellerCanAccompanyInf = relinkingPassenger.type !== 'INF' || !isAlreadyAccompanyInf;

		return (
			traveller.type === 'ADT' &&
			isOlderThanMinDate &&
			(traveller.id === relinkingToPassengerId || isRelinkingCldOrTravellerCanAccompanyInf)
		);
	};

	const adultAccompaniedMap = useMemo(() => getAdultAccompaniedMap(travellers), [travellers]);

	const adultsCount = useMemo(
		() => travellers.reduce((count, traveller) => (traveller.type === 'ADT' ? count + 1 : count), 0),
		[travellers]
	);

	const filteredTravellers = useMemo(
		() =>
			travellers.filter(traveller =>
				isRelinkingMode ? isAvailableForRelinking(traveller) : !traveller.linkedTraveller?.id
			),
		[travellers, adultAccompaniedMap, isRelinkingMode, relinkingPassenger]
	);

	const getIsChangeAvailable = useCallback(
		(travellerId: string) => {
			return !!changeTravellersAvailability?.find(changeTraveller => changeTraveller.id === travellerId)
				?.available;
		},
		[changeTravellersAvailability]
	);

	const goToChangePassengerHandler = (passengerId: string) => () => {
		goToChangePassenger?.(passengerId);
	};

	const showLinkTo = isRelinkingMode && onLinkTo && !isLoading;

	let passengerIndex = 0;

	return (
		<div className={cn(css.root, className, { [css.relink]: isRelinkingMode, [css.loading]: isLoading })}>
			{isRelinkingMode && (
				<Notification
					text={t(
						'After selecting a new adult, please double-check all services. Please choose an adult who does not sit on the emergency seat row.'
					)}
					status={'warning'}
					type={'context'}
				/>
			)}

			<div className={css.passengers}>
				{filteredTravellers.map(traveller => {
					const accompanied = adultAccompaniedMap.get(traveller.id);
					passengerIndex++;

					const isChangeAvailable = getIsChangeAvailable(traveller.id) && !!goToChangePassenger;

					return (
						<div
							key={passengerIndex}
							className={css.item}
							onClick={showLinkTo ? () => onLinkTo(traveller?.id) : undefined}
						>
							<Passenger
								index={passengerIndex}
								traveller={traveller}
								isRelinkingMode={isRelinkingMode}
								isDisabled={isLoading}
								selectedAsAccompanying={traveller.id === relinkingToPassengerId}
								onLinkTo={onLinkTo}
								onChangePassenger={
									isChangeAvailable && !isRelinkingMode
										? goToChangePassengerHandler(traveller.id)
										: undefined
								}
							/>

							{!isRelinkingMode && accompanied?.length && (
								<>
									<div className={css.accompanies}>
										<div className={css.accompanies__text}>{t('accompanies')}</div>
									</div>

									{accompanied.map(accompaniedPassenger => {
										passengerIndex++;

										const isChangeAvailable = getIsChangeAvailable(accompaniedPassenger.id);

										return (
											<Passenger
												key={passengerIndex}
												index={passengerIndex}
												traveller={accompaniedPassenger}
												isAccompanied
												isRelinkingAvailable={orderStatus !== OrderStatus.Cancelled}
												isDisabled={isLoading}
												onSetRelinkingMode={adultsCount > 1 ? onSetRelinkingMode : null}
												onChangePassenger={
													isChangeAvailable
														? goToChangePassengerHandler(accompaniedPassenger.id)
														: undefined
												}
											/>
										);
									})}
								</>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default PassengersDetails;
